/**
 * Sync reports date range (fromDate/toDate) with URL query params.
 * Enables shareable report links: /raporty?from=2026-04-01&to=2026-04-30
 *
 * Usage: useReportsUrlSync(fromDate, setFromDate, toDate, setToDate);
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const useReportsUrlSync = (fromDate, setFromDate, toDate, setToDate) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialSyncDone = useRef(false);

  // Only run on /raporty
  const onReports = location.pathname === '/raporty';

  // URL -> state (on first mount or when navigating in)
  useEffect(() => {
    if (!onReports) {
      initialSyncDone.current = false;
      return;
    }
    if (initialSyncDone.current) return;

    const params = new URLSearchParams(location.search);
    const urlFrom = params.get('from') || '';
    const urlTo = params.get('to') || '';
    if (urlFrom && urlFrom !== fromDate) setFromDate(urlFrom);
    if (urlTo && urlTo !== toDate) setToDate(urlTo);
    initialSyncDone.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReports, location.search]);

  // state -> URL (after initial sync only, to avoid wiping params on mount)
  useEffect(() => {
    if (!onReports || !initialSyncDone.current) return;

    const params = new URLSearchParams();
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    const search = params.toString() ? `?${params.toString()}` : '';
    if (search !== location.search) {
      navigate(`/raporty${search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, onReports]);
};
